/**
 * HotelsNearby — "Check hotels nearby" call-to-action card.
 *
 * Renders on the Order Confirmation and Invoice Confirmation pages.
 *
 * Responsive behaviour (driven by useMediaQuery):
 *  - Desktop (≥ 1024 px): 400 × 159, placed in the right sidebar column,
 *    separated from the main content by the existing vertical divider.
 *  - Tablet (768 – 1023 px): full-width card, left-aligned, placed below the
 *    main content after a horizontal divider (24 px gap each side).
 *  - Mobile (< 768 px): 335 × 159, centred horizontally, below the content.
 *
 * All dimensions, colours, typography and spacing values match the Figma spec
 * exactly. No inline styles — Tailwind utility classes only.
 */
import React from 'react';

import { ORDER_TEXTS } from '../constants/order.constants';

interface HotelsNearbyProps {
    /** URL to open when the "Book now" button is clicked. Defaults to '#'. */
    bookingUrl?: string;
}

const ensureAbsoluteUrl = (url: string) => {
    if (!url || url === '#') return url;
    // Check if URL already has a protocol or starts with //
    if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
        return url;
    }
    return `https://${url}`;
};

const HotelsNearby: React.FC<HotelsNearbyProps> = ({ bookingUrl = '#' }) => {
    const absoluteUrl = ensureAbsoluteUrl(bookingUrl);

    return (
        <div
            className={[
                'w-[335px] h-[159px] md:w-[400px]',
                'mx-auto md:mx-0',
                'bg-white shadow-[inset_0_0_0_1px_#D0D5DD] rounded-lg',
                'p-[24px]',
                'flex flex-col items-start gap-[45px]',
            ].join(' ')}
        >
            {/* ── Title ── */}
            <h3
                className={[
                    'font-noto text-[18px] font-semibold leading-[26px] tracking-[0px] text-[#002B66] w-full',
                ].join(' ')}
            >
                {ORDER_TEXTS.HOTEL_MAP_TITLE}
            </h3>

            {/* ── Book now button ── */}
            <button
                type="button"
                onClick={() => {
                    if (absoluteUrl !== '#') {
                        window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
                    }
                }}
                className={[
                    'w-[91px] h-[40px] bg-[#1A6FE5] rounded-[6px] px-4',
                    'flex items-center justify-center gap-[6px]',
                    'hover:bg-[#1560cc] focus-visible:outline focus-visible:outline-2',
                    'focus-visible:outline-offset-2 focus-visible:outline-[#1A6FE5]',
                    'transition-colors duration-150 cursor-pointer',
                ].join(' ')}
                aria-label={ORDER_TEXTS.HOTEL_MAP_ARIA}
            >
                <span
                    className={[
                        'font-noto text-[12px] font-bold leading-[18px] tracking-[0px] text-white',
                        'w-[59px] h-[18px] whitespace-nowrap',
                    ].join(' ')}
                >
                    {ORDER_TEXTS.HOTEL_MAP_BUTTON}
                </span>
            </button>
        </div>
    );
};

export default HotelsNearby;
